import { ROUTES } from "@/constants/routes";
import { RailCanvasHeroPreviewTooltip } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailCanvasHeroPreviewTooltip";
import { RailDisclosureIcon } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailChevron";
import { useRailRowMultiMenuGate } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailMultiSelectionMenu";
import {
  useRailRowSelection,
  useRegisterRailResource,
  type RailSelectionSource,
} from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailSelectionContext";
import { useRailRowContextMenu } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/useRailRowContextMenu";
import {
  ObjectActionMenu,
  useCanvasObjectActionHandlers,
  useObjectActions,
} from "@/object-actions";
import { useAttachCanvasTransferSubmenu } from "@/object-actions/transfer/useCanvasTransferSubmenu";
import { cn } from "@/utils";
import { route } from "@/utils/route";
import {
  getCanvasHeroImageUrl,
  RESOURCE_TAG_FAVORITE,
  type Canvas,
} from "@cohesive/api-client";
import { useThemeContext } from "@cohesive/ui-components";
import { Frame } from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

interface WorkspacesRailCanvasRowProps {
  canvas: Canvas;
  active?: boolean;
  /** Rail section this row lives in — drives the source-aware highlight. */
  section?: RailSelectionSource;
  onRename: (canvas: Canvas) => void;
  /** Open the "Move to workspace" dialog. */
  /** Open the canvas Share dialog (gated on can_manage_assignments). */
  onShare?: (canvas: Canvas) => void;
  onDelete?: (canvas: Canvas) => void;
  dragRef?: (element: HTMLElement | null) => void;
  dragProps?: React.HTMLAttributes<HTMLElement>;
  isDragging?: boolean;
  /**
   * When set, single-clicking the title selects AND toggles the canvas's
   * nested chats + files (a leading always-visible chevron is an instant
   * toggle too); double click opens. Omitted for flat lists (favorites /
   * shared), where there is nothing to expand and title click only selects.
   */
  expanded?: boolean;
  onToggleExpanded?: () => void;
}

export function WorkspacesRailCanvasRow({
  canvas,
  active,
  section = "canonical",
  onRename,
  onShare,
  onDelete,
  dragRef,
  dragProps,
  isDragging,
  expanded,
  onToggleExpanded,
}: WorkspacesRailCanvasRowProps) {
  const { mode } = useThemeContext();
  const navigate = useNavigate();
  const menu = useRailRowContextMenu();
  const label = canvas.name || "Untitled";
  const isFavorite = !!canvas.tags?.includes(RESOURCE_TAG_FAVORITE);
  // The preview must drop the moment the user opens the row: Radix's own
  // close-on-pointerdown never fires for the Open pill (it stops pointerdown
  // so the row's drag doesn't arm), and `active` — which suppresses the
  // preview of the open page — only flips once navigation lands. Re-entering
  // the row re-arms the preview (a no-longer-active row can be hovered again).
  const [previewDismissed, setPreviewDismissed] = useState(false);
  // Parent CanvasRowDropZone is the list-item host for contiguous chrome.
  const { selected, onActivate, rowKey, selectionAttrs } = useRailRowSelection(
    canvas.id,
    section,
    "canvas",
    false,
  );

  const registered = useMemo(
    () => ({ kind: "canvas" as const, data: canvas }),
    [canvas],
  );
  useRegisterRailResource("canvas", canvas.id, registered);

  const onContextMenu = useRailRowMultiMenuGate(rowKey, menu.onContextMenu);

  // Rails navigate: a plain click opens the canvas (activating the row so
  // the highlight follows); modifier clicks are selection gestures only.
  const handleTitleClick = (e: React.MouseEvent) => {
    onActivate(e);
    if (e.shiftKey || e.metaKey || e.ctrlKey || e.altKey) return;
    setPreviewDismissed(true);
    navigate(route(ROUTES.CANVAS.DETAIL, { canvasId: canvas.id }));
  };

  const canShare = !!onShare;
  const canDelete = !!onDelete;

  const handlers = useCanvasObjectActionHandlers({
    canvas,
    canShare,
    canDelete,
  });

  const { actions: baseActions } = useObjectActions({
    kind: { type: "canvas" },
    surface: "left-rail",
    scope: section === "favorites" ? "favorites" : "canonical",
    enabled: menu.open,
    state: {
      isFavorite,
      canShare,
      canDelete,
      canEdit: canvas.can_edit !== false,
    },
    handlers: {
      ...handlers,
      // Mirror the row click: activate rail selection before navigating so the
      // favorites/canonical highlight stays in sync when opening via the menu.
      open: () => {
        onActivate();
        setPreviewDismissed(true);
        navigate(route(ROUTES.CANVAS.DETAIL, { canvasId: canvas.id }));
      },
      rename: () => window.setTimeout(() => onRename(canvas), 0),
      // Submenu-only: the destination picker attached below is the
      // interaction; the old MoveCanvasDialog is retired.
      move: () => {},
      share: onShare
        ? () => window.setTimeout(() => onShare(canvas), 0)
        : undefined,
      delete: onDelete
        ? () => window.setTimeout(() => onDelete(canvas), 0)
        : undefined,
    },
  });

  // Move to opens the workspace picker inline; the dialog handler stays as the
  // fallback for surfaces that can't host a cascading submenu.
  const actions = useAttachCanvasTransferSubmenu(baseActions, canvas);

  return (
    // The whole row is the preview trigger: the preview's position then keys
    // off the row's full width (→ the rail's right edge), never overlapping
    // the trailing Open pill / kebab the title button stops short of.
    <RailCanvasHeroPreviewTooltip
      label={label}
      imageUrl={getCanvasHeroImageUrl(canvas, mode)}
      active={active}
      suppress={menu.open || previewDismissed}
    >
      <div
        ref={dragRef}
        data-rail-canvas-row
        // The route-active row announces itself; the drawer's auto-open
        // measures against it so expanding never pushes it below the fold.
        aria-current={active ? "page" : undefined}
        {...selectionAttrs}
        onContextMenu={onContextMenu}
        onPointerEnter={() => setPreviewDismissed(false)}
        className={cn(
          "group/row relative flex min-h-7 w-full items-center gap-2.5 px-2 pr-8 hover:bg-n-05",
          !selected && "rounded-md",
          selected && "bg-foreground/[0.04] text-n-90",
          isDragging && "cursor-grabbing opacity-40",
        )}
        {...dragProps}
      >
        {onToggleExpanded ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpanded();
            }}
            aria-expanded={expanded}
            aria-label={expanded ? "Collapse canvas" : "Expand canvas"}
            title={expanded ? "Collapse" : "Expand"}
            className="-m-1 inline-flex shrink-0 rounded-sm p-1 hover:bg-n-10"
          >
            <RailDisclosureIcon open={expanded} icon={Frame} />
          </button>
        ) : null}
        <button
          type="button"
          onClick={handleTitleClick}
          aria-expanded={onToggleExpanded ? expanded : undefined}
          /**
           * Don't take focus on mousedown: the default blurs the composer, so
           * its focus ring would drop here and spring back once a double-click
           * open refocused it. Click still fires on mouseup; keyboard is
           * untouched. Same reasoning as the workspace row's title button.
           */
          onMouseDown={(e) => e.preventDefault()}
          className={cn(
            // self-stretch: keep the full row height clickable.
            "flex min-w-0 flex-1 select-none items-center gap-2.5 self-stretch text-left outline-none focus-visible:rounded-sm focus-visible:outline-2 focus-visible:outline-rust focus-visible:outline-offset-[-2px]",
          )}
        >
          {/* The swap idiom reserves no chevron column — expandable rows carry
              their icon in the toggle button; leaf rows render it here. */}
          {!onToggleExpanded && (
            <Frame className="size-3.5 shrink-0 text-n-50" />
          )}
          <span className="min-w-0 flex-1 truncate text-base text-n-80">
            {label}
          </span>
        </button>

        <ObjectActionMenu
          actions={actions}
          kind={{ type: "canvas" }}
          name={label}
          menuController={menu}
          label={label}
        />
      </div>
    </RailCanvasHeroPreviewTooltip>
  );
}
